import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Check, CheckCircle2, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import {
  reviewClinicalDocument,
  trackContextualAIEvent,
  updateContextualAnalysisStatus,
} from "../../services/contextualAiApi";


export default function DocumentAIReviewCard({
  patientId,
  documentId,
  existingAnalysis,
  onAnalysisChange,
  onClose,
  onEditDocument,
}) {
  const [analysis, setAnalysis] = useState(existingAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const tracked = useRef(false);

  useEffect(() => setAnalysis(existingAnalysis || null), [existingAnalysis]);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackContextualAIEvent("ai_context_action_shown", {
      module: "documents",
      action: "preflight_review",
      metadata: { context_type: "document" },
    });
  }, []);

  const status = analysis?.status || "generated";
  const payload = analysis?.payload || {};
  const needsGeneration = !analysis || status === "stale" || status === "dismissed";

  const publish = (next) => {
    setAnalysis(next);
    onAnalysisChange?.(next);
  };

  const runReview = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    const startedAt = Date.now();
    trackContextualAIEvent("ai_context_action_clicked", {
      module: "documents",
      action: "preflight_review",
      metadata: { context_type: "document" },
    });
    try {
      const result = await reviewClinicalDocument({ patientId, documentId });
      publish(result);
      trackContextualAIEvent("ai_context_generation_success", {
        module: "documents",
        action: "preflight_review",
        outcome: "rules_only",
        duration_ms: Date.now() - startedAt,
        analysis_id: result?.id,
        metadata: { context_type: "document", status: result?.status },
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Não foi possível conferir este documento agora.");
      trackContextualAIEvent("ai_context_generation_failed", {
        module: "documents",
        action: "preflight_review",
        outcome: "error",
        duration_ms: Date.now() - startedAt,
        metadata: { context_type: "document", error_code: String(requestError?.response?.status || "request_failed") },
      });
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (nextStatus) => {
    if (!analysis?.id || loading) return;
    setLoading(true);
    try {
      const updated = await updateContextualAnalysisStatus(analysis.id, nextStatus);
      publish(updated);
      trackContextualAIEvent(nextStatus === "accepted" ? "ai_context_result_accepted" : "ai_context_result_dismissed", {
        module: "documents",
        action: "preflight_review",
        analysis_id: analysis.id,
        metadata: { context_type: "document", status: nextStatus },
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Não foi possível atualizar a conferência.");
    } finally {
      setLoading(false);
    }
  };

  if (needsGeneration) {
    return (
      <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" />
            <div>
              <p className="font-semibold text-gray-900">{status === "stale" ? "O documento mudou desde a última conferência" : "Confira antes de gerar ou enviar"}</p>
              <p className="mt-1 text-sm text-gray-600">A IA verifica o preenchimento sem alterar o conteúdo clínico.</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border bg-white px-3 py-2 text-sm">Fechar</button>
            <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Conferir documento
            </button>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      </div>
    );
  }

  const blockers = Array.isArray(payload.blockers) ? payload.blockers : [];
  const confirmations = Array.isArray(payload.confirmation_items) ? payload.confirmation_items : [];
  const documented = Array.isArray(payload.documented_items) ? payload.documented_items : [];

  return (
    <div className="mb-4 rounded-xl border border-purple-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-700" />
          <div>
            <p className="font-semibold text-gray-900">Conferência assistida do documento</p>
            <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${payload.ready_to_issue ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-900"}`}>
              {payload.ready_to_issue ? "Preenchimento essencial completo" : "Correções necessárias"}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-1 text-xs font-medium text-purple-700"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Conferir novamente</button>
          <button type="button" onClick={onClose} aria-label="Fechar"><X className="h-4 w-4 text-gray-500" /></button>
        </div>
      </div>

      <p className="mt-3 text-sm text-gray-700">{payload.summary}</p>
      {documented.length > 0 && <ul className="mt-3 space-y-1 text-sm text-green-800">{documented.map((item, index) => <li key={index}>✓ {item}</li>)}</ul>}
      {blockers.length > 0 && (
        <div className="mt-4 rounded-lg bg-red-50 p-3 text-red-900">
          <p className="flex items-center gap-2 text-sm font-semibold"><AlertCircle className="h-4 w-4" /> Corrija antes de emitir</p>
          <ul className="mt-2 space-y-1 text-sm">{blockers.map((item, index) => <li key={index}>• {item}</li>)}</ul>
        </div>
      )}
      {confirmations.length > 0 && <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{confirmations.map((item, index) => <p key={index}>• {item}</p>)}</div>}
      {payload.controlled_warning && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-800">{payload.controlled_warning}</div>}
      {payload.ready_to_issue && <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800"><CheckCircle2 className="h-4 w-4" /> Campos essenciais preenchidos; faça a conferência clínica final.</div>}
      <p className="mt-4 text-xs text-gray-500">{payload.disclaimer}</p>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

      <div className="mt-4 flex flex-wrap justify-end gap-2 border-t pt-3">
        {blockers.length > 0 && <button type="button" onClick={onEditDocument} className="rounded-lg border border-purple-200 px-3 py-1.5 text-sm text-purple-700">Abrir para corrigir</button>}
        {status === "generated" && <>
          <button type="button" onClick={() => changeStatus("dismissed")} disabled={loading} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm"><X className="h-4 w-4" /> Descartar</button>
          <button type="button" onClick={() => changeStatus("accepted")} disabled={loading || blockers.length > 0} className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"><Check className="h-4 w-4" /> Confirmar conferência</button>
        </>}
      </div>
    </div>
  );
}
